/**
 * Generic client-side plumbing for `POST /views/migrate/{migrator}` (and, by the same
 * shape, `POST /views/my-reservations/regenerate`): invoke a migrator, subscribe to its
 * streamed `MigratedItem`s, and expose incremental progress to the UI as they arrive.
 *
 * This intentionally knows nothing about *which* migrator is running or what its params
 * look like beyond `MigrateArg` (see migrationTypes.ts) — callers (Migration.tsx) own that.
 *
 * Modeled after the Firestore `onSnapshot`-backed streaming APIs in `firestoreModel.ts`
 * (`createFirestoreStreamApi`): a `query` endpoint whose `queryFn` just seeds the initial
 * cache value, with the real subscription/streaming work happening in
 * `onCacheEntryAdded`, pushing updates via `updateCachedData` as they come in and
 * cleaning up on `cacheEntryRemoved`. The source here is a chunked `fetch()` response
 * instead of a Firestore listener, since RTK Query's own `fetchBaseQuery` awaits
 * `response.json()` on the whole body and can't itself surface incremental progress
 * (see streamingJsonArrayParser.ts for why/how the chunks are parsed as they arrive).
 *
 * Each `migrate()` call should be its own run even if triggered twice with identical
 * params (e.g. clicking "Regenerate" again) — these are side-effecting POSTs, not
 * cacheable GETs — so callers should include a fresh `runId` (e.g. `Date.now()`) in the
 * arg each time to force a new cache entry rather than resubscribing to a finished one.
 */
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import { getMsalInstance } from "../../context/msalInstance";
import { RootState } from "../store";
import { MigratedItem, MigrateArg } from "./migrationTypes";
import { StreamingJsonArrayParser } from "./streamingJsonArrayParser";

export type MigrationStatus = "running" | "done" | "error";

export interface MigrationProgress {
    status: MigrationStatus;
    items: MigratedItem[];
    errorMessage?: string;
}

export type MigrationQueryArg = MigrateArg & {
    /** Cache-key salt only — not sent to the backend. See module doc above. */
    runId: number;
};

const emptyProgress = (): MigrationProgress => ({
    status: "running",
    items: [],
});

export const migrationStreamApi = createApi({
    reducerPath: "migrationStreamApi",
    baseQuery: fakeBaseQuery(),
    endpoints: (build) => ({
        migrate: build.query<MigrationProgress, MigrationQueryArg>({
            queryFn: () => ({ data: emptyProgress() }),

            async onCacheEntryAdded(arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved, getState }) {
                const controller = new AbortController();

                try {
                    await cacheDataLoaded;

                    const config = (getState() as RootState).config.config;
                    if (!config) {
                        throw new Error("No app config loaded yet");
                    }

                    const headers: Record<string, string> = { "Content-Type": "application/json" };

                    // Mirrors emptyApi.ts's prepareHeaders — same token acquisition, duplicated here
                    // because this uses a raw fetch() (for streaming reads) instead of fetchBaseQuery.
                    const instance = getMsalInstance();
                    const account = instance?.getActiveAccount() ?? instance?.getAllAccounts()[0];
                    const scope = config.msalConfig.scope;
                    if (instance && account && scope) {
                        const result = await instance.acquireTokenSilent({ scopes: [scope], account });
                        headers["authorization"] = `Bearer ${result.accessToken}`;
                    }

                    // Deliberately config.directApiEndpoint (Cloud Run directly), not apiEndpoint
                    // (the Firebase Hosting rewrite): Hosting buffers the whole response and caps
                    // the rewrite at a fixed 60s regardless of streaming, which is exactly the 502
                    // this endpoint's streaming was meant to avoid. See deploy/fb-app.tf.
                    const url = new URL(
                        `${config.directApiEndpoint}/api/views/migrate/${arg.migrator}`,
                        window.location.href
                    ).href;

                    const response = await fetch(url, {
                        method: "POST",
                        headers,
                        body: JSON.stringify(arg.params),
                        signal: controller.signal,
                    });

                    if (!response.ok || !response.body) {
                        const text = await response.text().catch(() => "");
                        throw new Error(`${response.status} ${response.statusText}${text ? `: ${text}` : ""}`);
                    }

                    const reader = response.body.getReader();
                    const decoder = new TextDecoder();
                    const parser = new StreamingJsonArrayParser<MigratedItem>();

                    for (;;) {
                        const { value, done } = await reader.read();
                        if (done) {
                            break;
                        }

                        const items = parser.push(decoder.decode(value, { stream: true }));
                        if (items.length > 0) {
                            updateCachedData((draft) => {
                                draft.items.push(...items);
                            });
                        }
                    }

                    updateCachedData((draft) => {
                        draft.status = "done";
                    });
                } catch (error: unknown) {
                    if (error instanceof DOMException && error.name === "AbortError") {
                        // Cache entry was removed (component unmounted / a new run superseded this
                        // one) — not a real failure, nothing to report.
                    } else {
                        console.error(`[migrationStreamApi] migrate(${arg.migrator}) failed:`, error);
                        updateCachedData((draft) => {
                            draft.status = "error";
                            draft.errorMessage = error instanceof Error ? error.message : String(error);
                        });
                    }
                }

                await cacheEntryRemoved;
                controller.abort();
            },
        }),
    }),
});

export const useMigrateLazyQuery = migrationStreamApi.endpoints.migrate.useLazyQuery;
